import React, { useState } from "react";
import {
  PortalBookingType,
  usePortalBookingQuery,
} from "../hooks/use_portal_booking_query";
import styles from "./portal_booking_list.module.css";
import { Tabs } from "../../../components/tab";
import { PortalBookingCard } from "./portal_booking_card";
import { Spacer } from "../../spacer";
import { useMediaQuery } from "../../../hooks";
import { CancelBookingDialog } from "./cancel_booking_dialog";
import { PulseLoader } from "../../../components/pulse_loader";
import { PortalButton } from "./portal_button";
import { PortalEmptyState } from "./portal_empty_state";
import { useParams } from "react-router-dom";

enum BookingTabs {
  UpcomingBookings = "Upcoming Bookings",
  ArchiveBookings = "Archive Bookings",
}

const bookingTabOptions = [
  {
    label: BookingTabs.UpcomingBookings,
    value: BookingTabs.UpcomingBookings,
  },
  {
    label: BookingTabs.ArchiveBookings,
    value: BookingTabs.ArchiveBookings,
  },
];

interface PortalMembershipPageParams {
  subscriptionId: string;
}

interface PortalBookingListProps {
  partnerId: string;
  bookingType?: PortalBookingType;
}

export function PortalBookingList({
  partnerId,
  bookingType,
}: PortalBookingListProps) {
  const { device } = useMediaQuery();
  const { subscriptionId } = useParams<PortalMembershipPageParams>();

  const [bookingTabValue, setBookingTabValue] = useState(
    BookingTabs.UpcomingBookings
  );
  const [cancelBookingId, setCancelBookingId] = useState<string>();

  const portalBookingQuery = usePortalBookingQuery(
    partnerId,
    bookingTabValue === BookingTabs.UpcomingBookings ? "upcoming" : "archived",
    bookingType,
    subscriptionId
  );

  const noBookingText =
    bookingTabValue === BookingTabs.ArchiveBookings
      ? "Your past and canceled bookings will appear here after you’ve booked."
      : "Your upcoming bookings will appear here once confirmed.";

  const emptyList = portalBookingQuery.isLoading ? (
    <div className={styles.centerLoading}>
      <PulseLoader />
    </div>
  ) : (
    <PortalEmptyState
      title="Ready for a change of scenery?"
      description={`Book a space and look forward to working from your new favourite spot. ${noBookingText}`}
    />
  );

  const cancellingBooking = portalBookingQuery.bookings.find(
    (b) => b.id === cancelBookingId
  );

  return (
    <div>
      <Tabs
        options={bookingTabOptions}
        value={bookingTabValue}
        onChange={setBookingTabValue}
      />
      <Spacer size={device === "mobile" ? 24 : 40} />
      {portalBookingQuery.bookings.length === 0 ? (
        emptyList
      ) : (
        <div className={styles.bookingList}>
          {portalBookingQuery.bookings
            .filter(
              (b) =>
                (bookingTabValue === BookingTabs.ArchiveBookings &&
                  b.isArchived) ||
                (bookingTabValue === BookingTabs.UpcomingBookings &&
                  !b.isArchived)
            )
            .map((b) => (
              <PortalBookingCard
                key={b.id}
                {...b}
                onCancel={() => setCancelBookingId(b.id)}
              />
            ))}
          <div className={styles.loadMoreBtnWrapper}>
            {portalBookingQuery.isLoading && <PulseLoader />}
            {portalBookingQuery.handleLoadMore && (
              <PortalButton
                appearance="text"
                onClick={portalBookingQuery.handleLoadMore}
              >
                {`Load more ${
                  bookingTabValue === BookingTabs.ArchiveBookings
                    ? "archive bookings"
                    : "upcoming bookings"
                }`}
              </PortalButton>
            )}
          </div>
        </div>
      )}

      {cancelBookingId && (
        <CancelBookingDialog
          show={!!cancelBookingId}
          onHide={() => setCancelBookingId(undefined)}
          bookingId={cancelBookingId}
          partnerId={partnerId}
          space={{
            name: cancellingBooking?.spaceName,
            id: cancellingBooking?.spaceId,
          }}
          bookingType={bookingType}
        />
      )}
    </div>
  );
}
